// src/pages/Catalog/CatalogPublicationPreviewPage.jsx
//
// Catalog Publication page
// ========================
//
// PURPOSE
// -------
// This page is the first operational frontend for the new Catalog publication flow.
//
// It allows an authenticated internal user to:
//
// 1) create a persistent preview batch
// 2) inspect recent publication heads
// 3) open one head and review its items
// 4) publish a prepared head
//
// WHY THIS PAGE MATTERS
// ---------------------
// Catalog CRUD alone is not enough for the business goal.
// The real publication workflow requires a controlled review layer before
// changes are pushed to WordPress.
//
// This page intentionally stays practical and explicit:
// - clear top-level actions
// - tabs for "Preview & Publish" vs "History"
// - filtered left-side list of publication heads
// - selected head detail on the right
// - field-level diff rendering for easier review
// - optional raw payload inspector per item
//
// IMPORTANT DATA NOTE
// -------------------
// The backend stores payload snapshots as LONGTEXT fields, for example:
// - before_payload_json
// - after_payload_json
// - diff_json
//
// That means the frontend must safely parse these strings before display.
//
// UX STRATEGY
// -----------
// This page intentionally does not try to be a generic JSON inspector.
// It is an operational review screen for internal staff.
//
// Therefore:
// - diff is presented first in a readable field-level way
// - raw payload is optional and collapsible
// - the page favors clarity over maximum abstraction
//
// I18N NOTE
// ---------
// Translation keys are used only for frontend-owned labels, filters, helper
// messages and confirmation text. Backend publication codes such as status,
// change_type, entity_type, target_system and entity_scope remain stable payload
// values; this page translates them only at display time.
//

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./CatalogPublicationPreviewPage.css";

import {
  createPublicationPreview,
  fetchPublicationHeads,
  fetchPublicationHead,
  publishPublicationHead,
} from "../../services/catalogPublicationService";

/* -------------------------------------------------------------------------- */
/* Local helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Safely parse JSON text coming from LONGTEXT backend fields.
 *
 * Return:
 * - parsed object on success
 * - null on empty/invalid input
 */
function safeParseJson(textValue) {
  if (!textValue) return null;

  try {
    return JSON.parse(textValue);
  } catch {
    return null;
  }
}

/**
 * Pretty-print raw JSON text for optional inspector blocks.
 *
 * If parsing fails, return the original value as a string.
 */
function prettyPrintJsonText(textValue) {
  if (!textValue) return "—";

  try {
    return JSON.stringify(JSON.parse(textValue), null, 2);
  } catch {
    return String(textValue);
  }
}

/**
 * Format a date/time value for stable UI display.
 */
function formatDateTime(value) {
  if (!value) return "—";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);

    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * Format a scalar value for readable diff rendering.
 *
 * This helper intentionally keeps display simple and operational:
 * - null / undefined / empty -> "—"
 * - boolean -> true / false
 * - object -> JSON string
 * - everything else -> String(...)
 */
function formatScalar(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * CSS class helper for head status badges.
 */
function getHeadStatusClass(status) {
  const s = String(status || "").toLowerCase();

  switch (s) {
    case "published":
      return "cpp-badge cpp-badge--success";
    case "preview_ready":
      return "cpp-badge cpp-badge--info";
    case "failed":
      return "cpp-badge cpp-badge--danger";
    case "draft":
      return "cpp-badge cpp-badge--muted";
    case "cancelled":
      return "cpp-badge cpp-badge--muted";
    default:
      return "cpp-badge cpp-badge--muted";
  }
}

/**
 * CSS class helper for per-item publish status pills.
 */
function getItemStatusClass(status) {
  const s = String(status || "").toLowerCase();

  switch (s) {
    case "published":
      return "cpp-pill cpp-pill--success";
    case "pending":
      return "cpp-pill cpp-pill--info";
    case "failed":
      return "cpp-pill cpp-pill--danger";
    case "skipped":
      return "cpp-pill cpp-pill--muted";
    default:
      return "cpp-pill cpp-pill--muted";
  }
}

/**
 * CSS class helper for item change type pills.
 */
function getChangeTypeClass(changeType) {
  const s = String(changeType || "").toLowerCase();

  switch (s) {
    case "create":
      return "cpp-pill cpp-pill--success";
    case "update":
      return "cpp-pill cpp-pill--warning";
    case "remove":
      return "cpp-pill cpp-pill--danger";
    default:
      return "cpp-pill cpp-pill--muted";
  }
}

/**
 * Detect whether a diff node is a terminal "before/after" leaf.
 *
 * Example leaf:
 *   { before: "A", after: "B" }
 *
 * Everything else is treated as a nested tree that still needs flattening.
 */
function isDiffLeaf(node) {
  if (!node || typeof node !== "object" || Array.isArray(node)) {
    return false;
  }

  const keys = Object.keys(node);

  return (
    keys.includes("before") &&
    keys.includes("after") &&
    keys.every((k) => k === "before" || k === "after")
  );
}

/**
 * Flatten a nested diff tree into field rows.
 *
 * Why this is useful:
 * - backend diff can contain nested objects
 * - nested trees are harder to review inline in a dense operational table
 * - a field-path list is usually much easier to scan
 *
 * Example nested diff:
 *   {
 *     section: {
 *       name_public: { before: "A", after: "B" }
 *     }
 *   }
 *
 * becomes:
 *   [
 *     {
 *       path: "section.name_public",
 *       before: "A",
 *       after: "B"
 *     }
 *   ]
 */
function flattenDiffRows(node, parentPath = [], acc = []) {
  if (node == null) {
    return acc;
  }

  if (isDiffLeaf(node)) {
    acc.push({
      path: parentPath.join(".") || "(root)",
      before: node.before,
      after: node.after,
    });
    return acc;
  }

  if (typeof node !== "object" || Array.isArray(node)) {
    acc.push({
      path: parentPath.join(".") || "(root)",
      before: null,
      after: node,
    });
    return acc;
  }

  for (const [key, value] of Object.entries(node)) {
    flattenDiffRows(value, [...parentPath, key], acc);
  }

  return acc;
}

/**
 * Build a simple searchable text snapshot for a publication head.
 *
 * Used for client-side search in the history list.
 */
function buildHeadSearchText(head) {
  return [
    head?.id,
    head?.status,
    head?.target_system,
    head?.entity_scope,
    head?.createdAt,
    head?.publishedAt,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Build a simple searchable text snapshot for an item row.
 *
 * Used for client-side filtering of selected head items.
 */
function buildItemSearchText(item) {
  return [
    item?.entity_type,
    item?.entity_label,
    item?.context_label,
    item?.status,
    item?.change_type,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function CatalogPublicationPreviewPage() {
  const { t } = useTranslation(["common", "catalog"]);

  /* ------------------------------------------------------------------------ */
  /* Main page state                                                           */
  /* ------------------------------------------------------------------------ */

  const [activeTab, setActiveTab] = useState("preview"); // preview | history

  const [heads, setHeads] = useState([]);
  const [selectedHeadPayload, setSelectedHeadPayload] = useState(null);

  const [loadingHeads, setLoadingHeads] = useState(false);
  const [loadingHeadDetail, setLoadingHeadDetail] = useState(false);
  const [creatingPreview, setCreatingPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /**
   * Collapsible raw payload inspector per item.
   *
   * We keep it as a dictionary instead of a Set to keep React state updates simple.
   */
  const [rawOpenByItemId, setRawOpenByItemId] = useState({});

  /* ------------------------------------------------------------------------ */
  /* Head-list filters                                                         */
  /* ------------------------------------------------------------------------ */

  /**
   * Preview tab list controls:
   * - default status: show working heads
   * - limit smaller than history by default
   */
  const [previewStatusFilter, setPreviewStatusFilter] = useState("preview_ready");
  const [previewLimit, setPreviewLimit] = useState(20);
  const [previewSearch, setPreviewSearch] = useState("");

  /**
   * History tab list controls:
   * - default to published heads
   * - slightly larger history window
   */
  const [historyStatusFilter, setHistoryStatusFilter] = useState("published");
  const [historyLimit, setHistoryLimit] = useState(30);
  const [historySearch, setHistorySearch] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Selected-head item filters                                                */
  /* ------------------------------------------------------------------------ */

  const [itemSearch, setItemSearch] = useState("");
  const [itemChangeFilter, setItemChangeFilter] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Derived current tab state                                                 */
  /* ------------------------------------------------------------------------ */

  const currentStatusFilter =
    activeTab === "preview" ? previewStatusFilter : historyStatusFilter;

  const currentLimit = activeTab === "preview" ? previewLimit : historyLimit;

  const currentHeadSearch =
    activeTab === "preview" ? previewSearch : historySearch;

  const selectedHeadId = selectedHeadPayload?.head?.id || null;
  const selectedHead = selectedHeadPayload?.head || null;
  const selectedItems = Array.isArray(selectedHeadPayload?.items)
    ? selectedHeadPayload.items
    : [];
  const selectedSummary = selectedHeadPayload?.summary || null;

  /* ------------------------------------------------------------------------ */
  /* Data loading helpers                                                      */
  /* ------------------------------------------------------------------------ */

  /**
   * Load one selected head detail including all items and summary.
   *
   * This is reused from:
   * - initial bootstrap
   * - clicking on a head in the left list
   * - refresh current action
   * - after publish
   */
  const loadHeadDetail = useCallback(async (headId) => {
    if (!headId) return;

    setLoadingHeadDetail(true);
    setError("");

    try {
      const payload = await fetchPublicationHead(headId);
      setSelectedHeadPayload(payload);
    } catch (e) {
      setError(e?.message || t("catalog:publication.messages.loadDetailFailed"));
    } finally {
      setLoadingHeadDetail(false);
    }
  }, [t]);

  /**
   * Load publication heads for the currently active tab/filter state.
   *
   * IMPORTANT
   * ---------
   * Search is intentionally client-side only.
   * Server-side filtering currently uses:
   * - status
   * - limit
   *
   * This keeps backend simple while still providing useful list narrowing.
   */
  const loadHeads = useCallback(
    async ({ keepSelection = true, autoSelectFirst = false } = {}) => {
      setLoadingHeads(true);
      setError("");

      try {
        const result = await fetchPublicationHeads({
          targetSystem: "wordpress",
          entityScope: "catalog",
          status: currentStatusFilter || null,
          limit: currentLimit,
        });

        const items = Array.isArray(result?.items) ? result.items : [];
        setHeads(items);

        const currentStillVisible =
          keepSelection &&
          selectedHeadId &&
          items.some((head) => Number(head.id) === Number(selectedHeadId));

        if (!currentStillVisible) {
          if (items.length > 0 && autoSelectFirst) {
            await loadHeadDetail(items[0].id);
          } else if (items.length === 0) {
            setSelectedHeadPayload(null);
          }
        }
      } catch (e) {
        setError(e?.message || t("catalog:publication.messages.loadHeadsFailed"));
      } finally {
        setLoadingHeads(false);
      }
    },
    [currentLimit, currentStatusFilter, loadHeadDetail, selectedHeadId, t]
  );

  /* ------------------------------------------------------------------------ */
  /* Bootstrap and reactive loading                                            */
  /* ------------------------------------------------------------------------ */

  /**
   * Initial bootstrap.
   *
   * On first mount:
   * - load heads for the default tab
   * - auto-open the first returned head if available
   */
  useEffect(() => {
    loadHeads({ keepSelection: false, autoSelectFirst: true });
  }, []); // intentionally only once on first mount

  /**
   * Reload list when tab-specific server-side filters change.
   *
   * We intentionally do not trigger reload on text search changes,
   * because those are handled client-side.
   */
  useEffect(() => {
    loadHeads({ keepSelection: true, autoSelectFirst: true });
  }, [activeTab, currentStatusFilter, currentLimit]);

  /* ------------------------------------------------------------------------ */
  /* Actions                                                                   */
  /* ------------------------------------------------------------------------ */

  /**
   * Build a new persistent preview batch.
   *
   * After success:
   * - open Preview tab
   * - select the new head immediately
   * - refresh the left list
   */
  async function handleCreatePreview() {
    setCreatingPreview(true);
    setError("");
    setSuccess("");

    try {
      const payload = await createPublicationPreview({
        targetSystem: "wordpress",
        entityScope: "catalog",
      });

      setActiveTab("preview");
      setSelectedHeadPayload(payload);
      setSuccess(t("catalog:publication.messages.previewCreated"));

      await loadHeads({ keepSelection: true, autoSelectFirst: false });
    } catch (e) {
      setError(e?.message || t("catalog:publication.messages.previewCreateFailed"));
    } finally {
      setCreatingPreview(false);
    }
  }

  /**
   * Refresh currently selected head from backend.
   *
   * This is useful after publish or after backend-side changes.
   */
  async function handleRefreshCurrent() {
    if (!selectedHeadId) return;

    setError("");
    setSuccess("");

    try {
      await loadHeadDetail(selectedHeadId);
      setSuccess(t("catalog:publication.messages.detailRefreshed"));
    } catch (e) {
      setError(e?.message || t("catalog:publication.messages.refreshFailed"));
    }
  }

  /**
   * Publish the currently selected preview-ready head.
   *
   * IMPORTANT
   * ---------
   * This publishes the already stored batch.
   * It does NOT create a new preview during publish.
   */
  async function handlePublishCurrent() {
    if (!selectedHead?.id) {
      setError(t("catalog:publication.messages.noHeadSelected"));
      return;
    }

    if (String(selectedHead.status) !== "preview_ready") {
      setError(t("catalog:publication.messages.onlyPreviewReady"));
      return;
    }

    const confirmed = window.confirm(
      t("catalog:publication.confirmPublish", { id: selectedHead.id })
    );

    if (!confirmed) return;

    setPublishing(true);
    setError("");
    setSuccess("");

    try {
      const payload = await publishPublicationHead(selectedHead.id);

      setSelectedHeadPayload(payload);
      setSuccess(t("catalog:publication.messages.published"));

      await loadHeads({ keepSelection: true, autoSelectFirst: false });
    } catch (e) {
      setError(e?.message || t("catalog:publication.messages.publishFailed"));
    } finally {
      setPublishing(false);
    }
  }

  /**
   * Toggle the optional raw payload inspector for one item row.
   */
  function toggleRawInspector(itemId) {
    setRawOpenByItemId((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }

  /* ------------------------------------------------------------------------ */
  /* Derived UI data                                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * Client-side filtered head list.
   *
   * Why keep search client-side:
   * - current backend API does not need extra complexity yet
   * - recent head list is intentionally capped anyway
   * - user gets instant UI feedback
   */
  const visibleHeads = useMemo(() => {
    const term = String(currentHeadSearch || "").trim().toLowerCase();

    if (!term) return heads;

    return heads.filter((head) => buildHeadSearchText(head).includes(term));
  }, [heads, currentHeadSearch]);

  /**
   * Summary cards shown for currently selected head.
   */
  const summaryCards = useMemo(() => {
    if (!selectedSummary) return [];

    return [
      { label: t("catalog:publication.summary.totalItems"), value: selectedSummary.total_items ?? 0 },
      { label: t("catalog:publication.summary.create"), value: selectedSummary.create_count ?? 0 },
      { label: t("catalog:publication.summary.update"), value: selectedSummary.update_count ?? 0 },
      { label: t("catalog:publication.summary.remove"), value: selectedSummary.remove_count ?? 0 },
      { label: t("catalog:publication.summary.pending"), value: selectedSummary.pending_count ?? 0 },
      { label: t("catalog:publication.summary.published"), value: selectedSummary.published_count ?? 0 },
      { label: t("catalog:publication.summary.failed"), value: selectedSummary.failed_count ?? 0 },
    ];
  }, [selectedSummary, t]);

  /**
   * Client-side filtered selected-head items.
   *
   * Supports:
   * - free text search
   * - change type narrowing
   */
  const visibleItems = useMemo(() => {
    const term = String(itemSearch || "").trim().toLowerCase();

    return selectedItems.filter((item) => {
      const matchesSearch = !term || buildItemSearchText(item).includes(term);
      const matchesChange =
        !itemChangeFilter || String(item.change_type) === String(itemChangeFilter);

      return matchesSearch && matchesChange;
    });
  }, [selectedItems, itemSearch, itemChangeFilter]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="cpp-page">
      <div className="cpp-page__inner">
        <div className="cpp-page__header">
          <div>
            <h1>{t("catalog:publication.title")}</h1>
            <p>{t("catalog:publication.subtitle")}</p>
          </div>

          <div className="cpp-page__headerActions">
            <button
              type="button"
              className="cpp-btn cpp-btn--primary"
              onClick={handleCreatePreview}
              disabled={creatingPreview || publishing}
            >
              {creatingPreview ? t("catalog:publication.actions.buildingPreview") : t("catalog:publication.actions.buildPreview")}
            </button>

            <button
              type="button"
              className="cpp-btn"
              onClick={handleRefreshCurrent}
              disabled={
                !selectedHeadId || loadingHeadDetail || creatingPreview || publishing
              }
            >
              {t("catalog:publication.actions.refreshCurrent")}
            </button>

            <button
              type="button"
              className="cpp-btn cpp-btn--success"
              onClick={handlePublishCurrent}
              disabled={
                !selectedHead ||
                String(selectedHead?.status) !== "preview_ready" ||
                publishing ||
                creatingPreview
              }
            >
              {publishing ? t("catalog:publication.actions.publishing") : t("catalog:publication.actions.publishToWordPress")}
            </button>
          </div>
        </div>

        {error ? <div className="cpp-alert cpp-alert--error">{error}</div> : null}
        {success ? (
          <div className="cpp-alert cpp-alert--success">{success}</div>
        ) : null}

        {/* ------------------------------------------------------------------ */}
        {/* Top-level tabs                                                     */}
        {/* ------------------------------------------------------------------ */}
        <div className="cpp-tabs" role="tablist" aria-label={t("catalog:publication.aria.publicationViews")}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "preview"}
            className={`cpp-tab ${activeTab === "preview" ? "is-active" : ""}`}
            onClick={() => setActiveTab("preview")}
          >
            {t("catalog:publication.tabs.previewPublish")}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "history"}
            className={`cpp-tab ${activeTab === "history" ? "is-active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            {t("catalog:publication.tabs.history")}
          </button>
        </div>

        <div className="cpp-layout">
          {/* ---------------------------------------------------------------- */}
          {/* Left column: filtered heads list                                 */}
          {/* ---------------------------------------------------------------- */}
          <aside className="cpp-historyCard">
            <div className="cpp-historyCard__header">
              <div>
                <h2>
                  {activeTab === "preview"
                    ? t("catalog:publication.headList.previewTitle")
                    : t("catalog:publication.headList.historyTitle")}
                </h2>
                <p className="cpp-subtleText">
                  {activeTab === "preview"
                    ? t("catalog:publication.headList.previewDescription")
                    : t("catalog:publication.headList.historyDescription")}
                </p>
              </div>

              <button
                type="button"
                className="cpp-linkBtn"
                onClick={() =>
                  loadHeads({ keepSelection: true, autoSelectFirst: false })
                }
                disabled={loadingHeads || creatingPreview || publishing}
              >
                {loadingHeads ? t("catalog:publication.actions.loading") : t("catalog:publication.actions.reload")}
              </button>
            </div>

            <div className="cpp-filterBar">
              <div className="cpp-filterField">
                <label>{t("catalog:publication.filters.search")}</label>
                <input
                  type="text"
                  value={currentHeadSearch}
                  onChange={(e) => {
                    if (activeTab === "preview") {
                      setPreviewSearch(e.target.value);
                    } else {
                      setHistorySearch(e.target.value);
                    }
                  }}
                  placeholder={
                    activeTab === "preview"
                      ? t("catalog:publication.placeholders.searchPreviewHeads")
                      : t("catalog:publication.placeholders.searchHistory")
                  }
                />
              </div>

              <div className="cpp-filterField">
                <label>{t("catalog:publication.filters.status")}</label>
                <select
                  value={currentStatusFilter}
                  onChange={(e) => {
                    if (activeTab === "preview") {
                      setPreviewStatusFilter(e.target.value);
                    } else {
                      setHistoryStatusFilter(e.target.value);
                    }
                  }}
                >
                  <option value="">{t("catalog:publication.options.all")}</option>
                  <option value="draft">{t("catalog:publication.headStatuses.draft")}</option>
                  <option value="preview_ready">{t("catalog:publication.headStatuses.preview_ready")}</option>
                  <option value="published">{t("catalog:publication.headStatuses.published")}</option>
                  <option value="failed">{t("catalog:publication.headStatuses.failed")}</option>
                  <option value="cancelled">{t("catalog:publication.headStatuses.cancelled")}</option>
                </select>
              </div>

              <div className="cpp-filterField">
                <label>{t("catalog:publication.filters.limit")}</label>
                <select
                  value={currentLimit}
                  onChange={(e) => {
                    const next = Number(e.target.value);

                    if (activeTab === "preview") {
                      setPreviewLimit(next);
                    } else {
                      setHistoryLimit(next);
                    }
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="cpp-historyList">
              {visibleHeads.length === 0 ? (
                <div className="cpp-emptyBlock">
                  {t("catalog:publication.empty.noHeads")}
                </div>
              ) : (
                visibleHeads.map((head) => {
                  const isSelected = Number(selectedHeadId) === Number(head.id);

                  return (
                    <button
                      key={head.id}
                      type="button"
                      className={`cpp-historyItem ${isSelected ? "is-selected" : ""}`}
                      onClick={() => loadHeadDetail(head.id)}
                    >
                      <div className="cpp-historyItem__row">
                        <strong>{t("catalog:publication.headLabel", { id: head.id })}</strong>
                        <span className={getHeadStatusClass(head.status)}>
                          {t(`catalog:publication.headStatuses.${head.status}`, { defaultValue: head.status || "—" })}
                        </span>
                      </div>

                      <div className="cpp-historyItem__meta">
                        <div>{t("catalog:publication.meta.target", { value: head.target_system || "—" })}</div>
                        <div>{t("catalog:publication.meta.scope", { value: head.entity_scope || "—" })}</div>
                        <div>{t("catalog:publication.meta.created", { value: formatDateTime(head.createdAt) })}</div>
                        <div>{t("catalog:publication.meta.published", { value: formatDateTime(head.publishedAt) })}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* ---------------------------------------------------------------- */}
          {/* Right column: selected head detail                                */}
          {/* ---------------------------------------------------------------- */}
          <section className="cpp-detailCard">
            {!selectedHead ? (
              <div className="cpp-emptyBlock cpp-emptyBlock--large">
                {t("catalog:publication.empty.selectHead")}
              </div>
            ) : (
              <>
                <div className="cpp-detailCard__header">
                  <div>
                    <h2>{t("catalog:publication.headLabel", { id: selectedHead.id })}</h2>
                    <div className="cpp-headMeta">
                      <span className={getHeadStatusClass(selectedHead.status)}>
                        {t(`catalog:publication.headStatuses.${selectedHead.status}`, { defaultValue: selectedHead.status || "—" })}
                      </span>
                      <span>{t("catalog:publication.meta.target", { value: selectedHead.target_system || "—" })}</span>
                      <span>{t("catalog:publication.meta.scope", { value: selectedHead.entity_scope || "—" })}</span>
                      <span>{t("catalog:publication.meta.created", { value: formatDateTime(selectedHead.createdAt) })}</span>
                      <span>{t("catalog:publication.meta.published", { value: formatDateTime(selectedHead.publishedAt) })}</span>
                    </div>
                  </div>
                </div>

                <div className="cpp-summaryGrid">
                  {summaryCards.map((card) => (
                    <div key={card.label} className="cpp-summaryCard">
                      <div className="cpp-summaryCard__label">{card.label}</div>
                      <div className="cpp-summaryCard__value">{card.value}</div>
                    </div>
                  ))}
                </div>

                <div className="cpp-itemToolbar">
                  <div className="cpp-filterField cpp-filterField--wide">
                    <label>{t("catalog:publication.filters.searchItems")}</label>
                    <input
                      type="text"
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      placeholder={t("catalog:publication.placeholders.searchItems")}
                    />
                  </div>

                  <div className="cpp-filterField">
                    <label>{t("catalog:publication.filters.changeType")}</label>
                    <select
                      value={itemChangeFilter}
                      onChange={(e) => setItemChangeFilter(e.target.value)}
                    >
                      <option value="">{t("catalog:publication.options.all")}</option>
                      <option value="create">{t("catalog:publication.changeTypes.create")}</option>
                      <option value="update">{t("catalog:publication.changeTypes.update")}</option>
                      <option value="remove">{t("catalog:publication.changeTypes.remove")}</option>
                    </select>
                  </div>
                </div>

                <div className="cpp-tableArea">
                  <div className="cpp-tableWrap">
                    {loadingHeadDetail ? (
                      <div className="cpp-loadingOverlay">
                        <div className="cpp-loadingChip">
                          {t("catalog:publication.loadingDetail")}
                        </div>
                      </div>
                    ) : null}

                    <table className="cpp-table">
                      <thead>
                        <tr>
                          <th className="cpp-left">{t("catalog:publication.columns.entityType")}</th>
                          <th className="cpp-left">{t("catalog:publication.columns.context")}</th>
                          <th className="cpp-left">{t("catalog:publication.columns.label")}</th>
                          <th className="cpp-left">{t("catalog:publication.columns.change")}</th>
                          <th className="cpp-left">{t("catalog:publication.columns.itemStatus")}</th>
                          <th className="cpp-left">{t("catalog:publication.columns.fieldLevelDiff")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleItems.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="cpp-emptyCell">
                              {t("catalog:publication.empty.noItems")}
                            </td>
                          </tr>
                        ) : (
                          visibleItems.map((item) => {
                            const diff = safeParseJson(item.diff_json);
                            const diffRows = flattenDiffRows(diff);
                            const rawOpen = !!rawOpenByItemId[item.id];

                            return (
                              <tr key={item.id}>
                                <td className="cpp-left">{t(`catalog:publication.entityTypes.${item.entity_type}`, { defaultValue: item.entity_type || "—" })}</td>
                                <td className="cpp-left">{item.context_label || "—"}</td>
                                <td className="cpp-left">{item.entity_label || "—"}</td>
                                <td className="cpp-left">
                                  <span className={getChangeTypeClass(item.change_type)}>
                                    {t(`catalog:publication.changeTypes.${item.change_type}`, { defaultValue: item.change_type || "—" })}
                                  </span>
                                </td>
                                <td className="cpp-left">
                                  <span className={getItemStatusClass(item.status)}>
                                    {t(`catalog:publication.itemStatuses.${item.status}`, { defaultValue: item.status || "—" })}
                                  </span>
                                </td>
                                <td className="cpp-left cpp-diffCell">
                                  {diffRows.length > 0 ? (
                                    <div className="cpp-diffRows">
                                      <div className="cpp-diffRows__header">
                                        <div>{t("catalog:publication.diff.field")}</div>
                                        <div>{t("catalog:publication.diff.before")}</div>
                                        <div>{t("catalog:publication.diff.after")}</div>
                                      </div>

                                      {diffRows.map((row, index) => (
                                        <div
                                          key={`${item.id}-diff-${index}`}
                                          className="cpp-diffRows__row"
                                        >
                                          <div className="cpp-diffRows__path">
                                            {row.path === "(root)" ? t("catalog:publication.diff.rootPath") : row.path}
                                          </div>
                                          <div className="cpp-diffRows__before">
                                            {formatScalar(row.before)}
                                          </div>
                                          <div className="cpp-diffRows__after">
                                            {formatScalar(row.after)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="cpp-muted">{t("catalog:publication.diff.noData")}</span>
                                  )}

                                  <div className="cpp-rawToggleRow">
                                    <button
                                      type="button"
                                      className="cpp-linkBtn"
                                      onClick={() => toggleRawInspector(item.id)}
                                    >
                                      {rawOpen ? t("catalog:publication.actions.hideRawPayload") : t("catalog:publication.actions.showRawPayload")}
                                    </button>
                                  </div>

                                  {rawOpen ? (
                                    <div className="cpp-rawInspector">
                                      <div className="cpp-rawInspector__block">
                                        <div className="cpp-rawInspector__title">
                                          {t("catalog:publication.raw.beforePayload")}
                                        </div>
                                        <pre>
                                          {prettyPrintJsonText(item.before_payload_json)}
                                        </pre>
                                      </div>

                                      <div className="cpp-rawInspector__block">
                                        <div className="cpp-rawInspector__title">
                                          {t("catalog:publication.raw.afterPayload")}
                                        </div>
                                        <pre>
                                          {prettyPrintJsonText(item.after_payload_json)}
                                        </pre>
                                      </div>

                                      <div className="cpp-rawInspector__block">
                                        <div className="cpp-rawInspector__title">
                                          {t("catalog:publication.raw.rawDiffJson")}
                                        </div>
                                        <pre>{prettyPrintJsonText(item.diff_json)}</pre>
                                      </div>
                                    </div>
                                  ) : null}

                                  {item.error_message ? (
                                    <div className="cpp-itemError">
                                      {item.error_message}
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

